import { useEffect, useState, type ReactNode } from 'react';
import useSWR from 'swr';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { STALE_WITHDRAWAL_MS, withdrawalAgeMs } from '@entities/admin-notification';
import { useMerchantSettings } from '@features/merchant-settings';
import { confirmationView, formatRbacError, fundsLabelKey, getWithdrawalStatusVariant, isTerminalWithdrawalState, mapStateEventList, merchantHintKey, WithdrawalStatusBadge, withdrawalExplorerUrl } from '@entities/withdrawal';
import { ReconcileDialog } from '@features/withdrawal';
import { useApiServices } from '@shared/lib/use-api-services';
import type { WithdrawalStatus } from '@shared/api/contracts';
import type { WorkspaceScope, WorkspaceWithdrawal, WorkspaceWithdrawalDetails } from '@shared/api/contracts/workspace';
import { formatDateTime } from '@shared/lib/format-date-time';
import { cn } from '@shared/lib/utils';
import { useCopyToClipboard } from '@shared/lib/use-copy-to-clipboard';
import {
  Button,
  CopyableHash,
  Pagination,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  Skeleton,
  SkeletonStatus,
  StatusBanner,
  Tabs,
  TabsList,
  TabsTrigger,
  Textarea,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  TypographyMuted,
  useToast,
} from '@shared/ui';

const SCOPES: WorkspaceScope[] = ['pending', 'in_progress', 'attention', 'recent'];

const formatAge = (ms: number): string => {
  const total = Math.floor(ms / 1000);
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  const clock = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  return hours > 0 ? `${hours}:${clock}` : clock;
};

type ChipTone = 'muted' | 'warn' | 'fail';

const toneClass: Record<ChipTone, string> = {
  muted: 'bg-muted text-muted-foreground',
  warn: 'bg-pending text-pending-foreground',
  fail: 'bg-fail text-fail-foreground',
};

const attentionChips = (
  row: WorkspaceWithdrawal,
  t: (key: string, options?: { count: number }) => string,
): Array<{ key: string; label: string; tone: ChipTone }> => {
  const chips: Array<{ key: string; label: string; tone: ChipTone }> = [];
  if (row.flags.newAddress) chips.push({ key: 'new', label: t('workspace.flags.newAddress'), tone: 'muted' });
  if (row.flags.addressWhitelist === 'listed_on_hold') {
    chips.push({ key: 'hold', label: t('workspace.flags.whitelist.listed_on_hold'), tone: 'warn' });
  }
  if (row.flags.addressWhitelist === 'not_listed') {
    chips.push({ key: 'unlisted', label: t('workspace.flags.whitelist.not_listed'), tone: 'fail' });
  }
  if (row.flags.amlCached === 'risk') chips.push({ key: 'aml', label: t('workspace.flags.aml.risk'), tone: 'fail' });
  if (row.flags.amlCached === 'pending' || row.flags.amlCached === 'manual' || row.flags.amlCached === 'expired') {
    chips.push({ key: 'aml', label: t(`workspace.flags.aml.${row.flags.amlCached}`), tone: 'warn' });
  }
  if (row.flags.stuck) chips.push({ key: 'stuck', label: t('workspace.flags.stuck'), tone: 'fail' });
  if (row.flags.emergencyGrantActive) chips.push({ key: 'emergency', label: t('workspace.flags.emergency'), tone: 'warn' });
  if (row.walletKind === 'partner') chips.push({ key: 'partner', label: t('workspace.partnerWallet'), tone: 'muted' });
  if (row.flags.commentsCount) {
    chips.push({ key: 'comments', label: t('workspace.flags.comments', { count: row.flags.commentsCount }), tone: 'muted' });
  }
  return chips;
};

interface WorkspaceQueueProps {
  scope: WorkspaceScope;
  counts: { pending: number; in_progress: number; attention: number };
  rows: WorkspaceWithdrawal[];
  page: number;
  total: number;
  limit: number;
  serverTime: string;
  details: WorkspaceWithdrawalDetails | null;
  openId: number | null;
  expandedId: number | null;
  listLoading: boolean;
  listError: boolean;
  detailsError: boolean;
  checking: boolean;
  comment: string;
  onScope: (scope: WorkspaceScope) => void;
  onPage: (page: number) => void;
  onOpen: (id: number) => void;
  onExpand: (id: number) => void;
  onApprove: (row: WorkspaceWithdrawal) => void;
  onReject: (row: WorkspaceWithdrawal) => void;
  onEmergency: (row: WorkspaceWithdrawal) => void;
  onComment: (id: number) => void;
  onCommentChange: (value: string) => void;
  busy: boolean;
  canReconcile?: boolean;
  onReconciled?: () => void;
}

export function WorkspaceQueue(props: WorkspaceQueueProps) {
  const { t, i18n } = useTranslation('cabinet');
  const { t: tRbac } = useTranslation('rbac');
  const { toast } = useToast();
  const { copy } = useCopyToClipboard();
  const { openMerchantSettings } = useMerchantSettings();
  const [now, setNow] = useState(() => Date.now());
  const [reconcileOpen, setReconcileOpen] = useState(false);
  const services = useApiServices();
  const stateEvents = useSWR(
    props.openId ? ['admin-state-events', props.openId] : null,
    () => services.withdrawals.listAdminStateEvents(props.openId!),
  );

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  return (
    <section className="flex max-h-[min(42rem,70dvh)] min-h-[16rem] min-w-0 flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
      <div className="border-b border-border px-3 py-3 sm:px-4">
        <Tabs value={props.scope} onValueChange={(value) => props.onScope(value as WorkspaceScope)}>
          <TabsList className="h-auto w-full flex-wrap justify-start">
            {SCOPES.map((scope) => {
              const count = scope === 'recent' ? null : props.counts[scope];
              return (
                <TabsTrigger key={scope} value={scope}>
                  {t(`workspace.scopes.${scope}`)}
                  {count == null ? '' : ` ${count}`}
                </TabsTrigger>
              );
            })}
          </TabsList>
        </Tabs>
      </div>
      {props.checking ? <StatusBanner className="mx-3 mt-3" variant="pending">{t('workspace.checking')}</StatusBanner> : null}
      {props.listError ? <StatusBanner className="mx-3 mt-3" variant="destructive">{t('workspace.loadError')}</StatusBanner> : null}
      <div className="min-h-0 flex-1 space-y-2 overflow-y-auto p-3">
        {props.listLoading && !props.rows.length ? <QueueSkeleton label={t('workspace.loading')} /> : null}
        {!props.listLoading && !props.rows.length ? <TypographyMuted className="block py-10 text-center">{t('workspace.empty')}</TypographyMuted> : null}
        {props.rows.map((row) => {
          const age = withdrawalAgeMs(row.createdAt, props.serverTime, now);
          const variant = getWithdrawalStatusVariant(row.status as WithdrawalStatus);
          const stale = variant !== 'success' && variant !== 'danger' && age >= STALE_WITHDRAWAL_MS;
          const chips = attentionChips(row, t);
          const open = props.expandedId === row.id;
          return (
            <article
              key={row.id}
              className={cn(
                'rounded-xl border border-border bg-background p-3 transition-colors duration-fast ease-standard',
                open && 'bg-muted/40',
                stale && 'shadow-[inset_3px_0_0_0_rgb(var(--fail-foreground))]',
              )}
            >
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
                <div
                  role="button"
                  tabIndex={0}
                  className="min-w-0 flex-1 cursor-pointer text-left"
                  onClick={() => props.onExpand(row.id)}
                  onDoubleClick={() => props.onOpen(row.id)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      props.onExpand(row.id);
                    }
                  }}
                >
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <span className="text-lg font-semibold tabular-nums leading-6">{row.amount} {row.assetCode}</span>
                    <span className="text-sm text-muted-foreground">{row.network}</span>
                    <span className="text-xs text-muted-foreground">#{row.id}</span>
                    <span className="ml-auto flex items-center gap-2">
                      <span className={cn('font-mono text-xs tabular-nums', stale ? 'font-medium text-fail-foreground' : 'text-muted-foreground')}>{formatAge(age)}</span>
                      <WithdrawalStatusBadge status={row.status} state={row.state} audience="admin" />
                    </span>
                  </div>
                  <div className="mt-1 flex min-w-0 flex-wrap items-center gap-2">
                    <button
                      type="button"
                      className="max-w-full truncate text-sm underline-offset-2 hover:underline"
                      onClick={(event) => {
                        event.stopPropagation();
                        openMerchantSettings(row.merchant.id, { email: row.merchant.email });
                      }}
                    >
                      {row.merchant.email}
                    </button>
                    <span className="rounded-md bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">{t(`workspace.source.${row.source}`)}</span>
                  </div>
                  <div className="mt-2 max-w-full" onClick={(event) => event.stopPropagation()} onDoubleClick={(event) => event.stopPropagation()} onKeyDown={(event) => event.stopPropagation()}>
                    <CopyableHash value={row.address} onCopy={() => copy(row.address)} />
                  </div>
                  {chips.length ? (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {chips.map((chip) => (
                        <span key={chip.key} className={cn('rounded-md px-1.5 py-0.5 text-xs', toneClass[chip.tone])}>
                          {chip.label}
                        </span>
                      ))}
                    </div>
                  ) : null}
                </div>
                {props.scope === 'recent' ? null : (
                  <TooltipProvider delayDuration={200}>
                    <div className="flex shrink-0 gap-2 sm:w-36 sm:flex-col">
                      <ActionButton label={t('workspace.approve')} disabled={!row.actions.approve.allowed || props.busy} reason={row.actions.approve.reason} onClick={() => props.onApprove(row)} />
                      <ActionButton label={t('workspace.reject')} variant="destructive" disabled={!row.actions.reject.allowed || props.busy} reason={row.actions.reject.reason} onClick={() => props.onReject(row)} />
                    </div>
                  </TooltipProvider>
                )}
              </div>
              {open ? (
                <div className="mt-3 flex flex-col gap-2 border-t border-border pt-3 sm:flex-row sm:flex-wrap">
                  <Button type="button" size="sm" variant="outline" onClick={() => props.onOpen(row.id)}>{t('workspace.comment')}</Button>
                  <Button type="button" size="sm" variant="outline" onClick={() => openMerchantSettings(row.merchant.id, { email: row.merchant.email })}>
                    {t('workspace.openMerchant')}
                  </Button>
                  <Button type="button" size="sm" variant="outline" asChild>
                    <Link to={`/audit-log?targetType=withdrawal&targetId=${row.id}`}>{t('workspace.openAudit')}</Link>
                  </Button>
                  {row.actions.emergency.allowed ? (
                    <ActionButton label={t('workspace.emergency')} variant="outline" disabled={props.busy} reason={row.actions.emergency.reason} onClick={() => props.onEmergency(row)} />
                  ) : null}
                </div>
              ) : null}
            </article>
          );
        })}
      </div>
      <div className="border-t border-border px-3 py-2">
        <Pagination page={props.page} limit={props.limit} total={props.total} onPageChange={props.onPage} />
      </div>
      <Sheet modal={false} open={props.openId !== null} onOpenChange={(open) => !open && props.onOpen(0)}>
        <SheetContent hideOverlay className="lg:left-0 lg:right-auto lg:w-1/2 lg:border-l-0 lg:border-r">
          <SheetHeader>
            <SheetTitle>{props.details ? `#${props.details.id}` : t('workspace.card')}</SheetTitle>
          </SheetHeader>
          {props.details && props.details.id === props.openId ? (
            <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-4 pb-6">
              <div className="flex flex-wrap items-center gap-2">
                <p className="text-2xl font-semibold tabular-nums leading-8">{props.details.amount} {props.details.assetCode}</p>
                <span className="text-sm text-muted-foreground">{props.details.network}</span>
                <WithdrawalStatusBadge status={props.details.status} state={props.details.state} audience="admin" />
              </div>
              <TypographyMuted>{t(merchantHintKey(props.details.state, props.details.reasonCode))}</TypographyMuted>
              {fundsLabelKey(props.details.fundsReservationState) ? (
                <p className="text-sm text-foreground">{t(fundsLabelKey(props.details.fundsReservationState)!)}</p>
              ) : null}
              <div className="grid gap-3 sm:grid-cols-2">
                <DetailField label={t('workspace.fields.merchant')}>
                  <button
                    type="button"
                    className="break-all text-left underline-offset-2 hover:underline"
                    onClick={() => {
                      const merchant = props.details?.merchant;
                      if (!merchant) return;
                      openMerchantSettings(merchant.id, { email: merchant.email });
                    }}
                  >
                    {props.details.merchant.email}
                  </button>
                </DetailField>
                <DetailField label={t('workspace.fields.address')}>
                  <span className="break-all font-mono text-xs">{props.details.address}</span>
                </DetailField>
                <DetailField label={t('workspace.fields.wallet')}>{props.details.balance.walletName}</DetailField>
                <DetailField label={t('workspace.fields.balance')}>
                  {props.details.balance.asset.amount} {props.details.balance.asset.assetCode}
                </DetailField>
                <DetailField label={t('workspace.fields.liquidity')}>
                  <span className={cn(
                    props.details.liquidityHint.status === 'enough' && 'text-success-foreground',
                    props.details.liquidityHint.status === 'short' && 'text-fail-foreground',
                  )}
                  >
                    {t(`workspace.liquidity.${props.details.liquidityHint.status}`)}
                  </span>
                  <span className="mt-1 block text-xs font-normal text-muted-foreground">{t('workspace.liquidityHint')}</span>
                </DetailField>
                {props.details.txid ? (
                  <DetailField label={t('workspace.fields.txid')}>
                    {withdrawalExplorerUrl(props.details.network, props.details.txid) ? (
                      <a className="break-all font-mono text-xs underline-offset-2 hover:underline" href={withdrawalExplorerUrl(props.details.network, props.details.txid) ?? undefined} target="_blank" rel="noreferrer">{props.details.txid}</a>
                    ) : (
                      <span className="break-all font-mono text-xs">{props.details.txid}</span>
                    )}
                  </DetailField>
                ) : null}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {attentionChips(props.details, t).map((chip) => (
                  <span key={chip.key} className={cn('rounded-md px-1.5 py-0.5 text-xs', toneClass[chip.tone])}>{chip.label}</span>
                ))}
                {props.details.flags.addressWhitelist === 'listed' || props.details.flags.addressWhitelist === 'not_enabled' ? (
                  <span className="rounded-md bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
                    {t(`workspace.flags.whitelist.${props.details.flags.addressWhitelist}`)}
                  </span>
                ) : null}
                {props.details.flags.amlCached === 'none' || props.details.flags.amlCached === 'ok' || props.details.flags.amlCached === 'trusted' || props.details.flags.amlCached === 'unsupported' ? (
                  <span className="rounded-md bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
                    {t(`workspace.flags.aml.${props.details.flags.amlCached}`)}
                  </span>
                ) : null}
              </div>
              <section className="space-y-2">
                <h3 className="text-sm font-semibold">{t('withdrawals.history.title')}</h3>
                {fundsLabelKey(props.details.fundsReservationState) ? (
                  <TypographyMuted>{t(fundsLabelKey(props.details.fundsReservationState)!)}</TypographyMuted>
                ) : null}
                {props.details.statusChangedAt ? (
                  <TypographyMuted>{formatDateTime(props.details.statusChangedAt, i18n.language)}</TypographyMuted>
                ) : null}
                {confirmationView(props.details.confirmations).kind === 'count' ? (
                  <p className="text-sm text-foreground">
                    {t('withdrawals.confirmations.count', {
                      current: confirmationView(props.details.confirmations).current,
                      required: confirmationView(props.details.confirmations).required,
                    })}
                  </p>
                ) : null}
                {confirmationView(props.details.confirmations).kind === 'finality' ? (
                  <p className="text-sm text-foreground">{t('withdrawals.confirmations.finality')}</p>
                ) : null}
                {stateEvents.error ? <TypographyMuted>{t('withdrawals.history.error')}</TypographyMuted> : null}
                {!stateEvents.error && mapStateEventList(stateEvents.data).length === 0 ? (
                  <TypographyMuted>
                    {t(props.details.state ? 'withdrawals.history.empty' : 'withdrawals.history.incomplete')}
                  </TypographyMuted>
                ) : null}
                {mapStateEventList(stateEvents.data).map((step) => (
                  <div key={`${step.at}-${step.state}`} className="rounded-lg bg-muted/50 px-3 py-2">
                    <WithdrawalStatusBadge status={step.state} state={step.state} audience="admin" />
                    <p className="mt-1 text-xs text-muted-foreground">{formatDateTime(step.at, i18n.language)}</p>
                  </div>
                ))}
                {props.canReconcile && props.details.stateVersion != null && !isTerminalWithdrawalState(props.details.state) ? (
                  <Button type="button" variant="outline" size="sm" onClick={() => setReconcileOpen(true)}>
                    {t('withdrawals.reconcile.action')}
                  </Button>
                ) : null}
              </section>
              <section className="space-y-2">
                <h3 className="text-sm font-semibold">{t('workspace.history')}</h3>
                {props.details.addressHistory.length ? props.details.addressHistory.map((item) => (
                  <button key={item.id} type="button" className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm transition-colors duration-fast ease-standard hover:bg-muted" onClick={() => props.onOpen(item.id)}>
                    <span className="tabular-nums">#{item.id} · {item.amount} {item.assetCode}</span>
                    <WithdrawalStatusBadge status={item.status} state={item.state} audience="admin" />
                  </button>
                )) : <TypographyMuted>{t('workspace.empty')}</TypographyMuted>}
              </section>
              <section className="space-y-2">
                <h3 className="text-sm font-semibold">{t('workspace.timeline')}</h3>
                <ol className="space-y-3 border-l border-border pl-3">
                  {props.details.timeline.map((event) => (
                    <li key={event.id} className="text-sm">
                      <p>{t(`auditLog.events.${event.eventType}`, { defaultValue: event.eventType })}</p>
                      <p className="text-xs text-muted-foreground">
                        {formatDateTime(event.occurredAt, i18n.language)}
                        {event.reason ? ` · ${event.reason}` : ''}
                      </p>
                    </li>
                  ))}
                </ol>
                <Link className="inline-flex text-sm text-primary underline-offset-2 hover:underline" to={`/audit-log?targetType=withdrawal&targetId=${props.details.id}`}>{t('workspace.openAudit')}</Link>
              </section>
              <section className="space-y-2">
                <h3 className="text-sm font-semibold">{t('workspace.comments')}</h3>
                <ul className="space-y-2">
                  {props.details.comments.map((item) => (
                    <li key={item.id} className="rounded-lg bg-muted/70 px-3 py-2">
                      <p className="text-xs text-muted-foreground">{item.author.email} · {formatDateTime(item.createdAt, i18n.language)}</p>
                      <p className="mt-1 text-sm">{item.text}</p>
                    </li>
                  ))}
                </ul>
                <Textarea value={props.comment} maxLength={1000} rows={3} onChange={(event) => props.onCommentChange(event.target.value)} />
                <Button type="button" size="sm" disabled={props.busy || props.comment.trim().length < 1} onClick={() => props.onComment(props.details!.id)}>
                  {t('workspace.commentSend')}
                </Button>
              </section>
            </div>
          ) : props.detailsError ? (
            <StatusBanner className="mx-4" variant="destructive">{t('workspace.loadError')}</StatusBanner>
          ) : (
            <DrawerSkeleton label={t('workspace.loading')} />
          )}
        </SheetContent>
      </Sheet>
      <ReconcileDialog
        open={reconcileOpen}
        loading={props.busy}
        onOpenChange={setReconcileOpen}
        onConfirm={async (reason) => {
          if (!props.details || props.details.stateVersion == null) return;
          try {
            await services.withdrawals.reconcileWithdrawal(props.details.id, {
              reason,
              expectedStateVersion: props.details.stateVersion,
            });
            toast(t('withdrawals.reconcile.success'), 'success');
            setReconcileOpen(false);
            props.onReconciled?.();
            void stateEvents.mutate();
          } catch (error) {
            toast(formatRbacError(error, tRbac, t('withdrawals.reconcile.error')), 'error');
            props.onReconciled?.();
          }
        }}
      />
    </section>
  );
}

function QueueSkeleton({ label }: { label: string }) {
  return (
    <SkeletonStatus className="space-y-2" label={label}>
      {Array.from({ length: 5 }).map((_, index) => (
        <div key={index} className="space-y-3 rounded-xl border border-border p-3">
          <div className="flex items-center gap-2">
            <Skeleton className="h-4 w-12" />
            <Skeleton className="h-4 w-40" />
            <Skeleton className="ml-auto h-4 w-28" />
          </div>
          <div className="flex items-center gap-2">
            <Skeleton className="h-4 w-36" />
            <Skeleton className="h-4 w-14" />
            <Skeleton className="h-5 w-20 rounded-full" />
          </div>
        </div>
      ))}
    </SkeletonStatus>
  );
}

function DrawerSkeleton({ label }: { label: string }) {
  return (
    <SkeletonStatus className="space-y-4 px-4 pb-6" label={label}>
      <Skeleton className="h-5 w-40" />
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-2/3" />
      <Skeleton className="h-16 w-full" />
      <Skeleton className="h-24 w-full" />
    </SkeletonStatus>
  );
}

function DetailField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0 rounded-lg bg-muted/50 px-3 py-2">
      <p className="text-xs text-muted-foreground">{label}</p>
      <div className="mt-0.5 text-sm font-medium">{children}</div>
    </div>
  );
}

function ActionButton({
  label,
  reason,
  disabled,
  variant = 'default',
  onClick,
}: {
  label: string;
  reason?: string;
  disabled: boolean;
  variant?: 'default' | 'destructive' | 'outline';
  onClick: () => void;
}) {
  const { t } = useTranslation('cabinet');
  const button = (
    <Button type="button" size="sm" variant={variant} className="w-full" disabled={disabled} onClick={onClick}>
      {label}
    </Button>
  );
  if (!disabled || !reason) return button;
  return (
    <Tooltip>
      <TooltipTrigger asChild><span className="inline-flex w-full">{button}</span></TooltipTrigger>
      <TooltipContent>{t(`workspace.reason.${reason}`, { defaultValue: t('workspace.reason.unknown') })}</TooltipContent>
    </Tooltip>
  );
}
